import React, { useEffect, useRef } from 'react';
import { Box, Flex, TextV2 } from '@withjoy/joykit';
import { pxToRem } from '@withjoy/joykit/theme';
import { AccommodationCard } from '../../Accommodations.types';
import { HotelPhotoCarousel } from '@shared/components/HotelPhotoCarousel';
import { DEFAULT_HOTEL_IMAGE } from '@shared/utils/constants';
import { DistanceStatistics } from '@shared/components/DistanceStatistics';
import { useTranslation } from '@shared/core';
import { Clock } from '@withjoy/joykit/icons';
import { useEventPlaces } from '@shared/utils/eventPlace';
import { useUserConsent } from '@shared/utils/userConsent';
import { useQueryParams } from '@shared/utils/hooks/useQueryParams';
import type { TravelDistancesBetweenPointsResponseFragment } from '@graphql/generated';

export const Thumbnail = ({
  hotel,
  accommodationPhotos,
  renderTags,
}: Readonly<{
  hotel: AccommodationCard;
  accommodationPhotos: ReadonlyArray<{ src: string; alt: string }>;
  renderTags: () => React.ReactNode;
}>) => {
  return (
    <Box
      aspectRatio="3/2"
      width="100%"
      maxHeight={pxToRem(160)}
      position={'relative'}
      backgroundColor={'mono2'}
      overflow={'hidden'}
      borderRadius={pxToRem(12)}
    >
      <HotelPhotoCarousel
        hotel={hotel}
        images={accommodationPhotos.length > 0 ? accommodationPhotos : [{ src: hotel?.photoUrl || DEFAULT_HOTEL_IMAGE, alt: hotel.displayName || '' }]}
        context="Accommodations.Thumbnail"
        aspectRatio="3/2"
        navigation={{ variant: 'minimal', visible: 'default' }}
      />
      {renderTags()}
    </Box>
  );
};

interface TagsOnThumbnailProps
  extends Readonly<{
    isFirstTimeAutoPopulation: boolean;
    hideTravelTime: boolean;
    venueGooglePlaceId?: Maybe<string>;
    distances?: Maybe<TravelDistancesBetweenPointsResponseFragment>;
    milesToVenue?: Maybe<number>;
  }> {}

/**
 * A tag that displays a message about a hotel being newly added.
 * @returns A tag that displays a message about a hotel being newly added.
 */
export const TagsOnThumbnail = ({ isFirstTimeAutoPopulation, hideTravelTime, venueGooglePlaceId, distances, milesToVenue }: TagsOnThumbnailProps) => {
  // Should only show the newly added tag once per session.
  const hasSeenNewlyAddedTagRef = useRef<boolean>(false);

  const { venueInfo } = useEventPlaces();
  const { getUserConsent } = useUserConsent();
  const { tq } = useTranslation('travelMap');
  const queryParams = useQueryParams();

  // Check for showNewTag query parameter to force showing the tag even on first population
  const shouldForceShowTag = queryParams.showNewTag === 'true';

  const hasSeenAccommodationsTour = getUserConsent('hasSeenAccommodationsTour');
  const isNewlyAdded =
    hasSeenNewlyAddedTagRef.current && venueInfo.hasVenue && venueInfo.googlePlaceId === venueGooglePlaceId && (shouldForceShowTag || !isFirstTimeAutoPopulation);

  // Mark the newly added tag as seen after user sees accommodations tour
  useEffect(() => {
    if (!hasSeenAccommodationsTour && !hasSeenNewlyAddedTagRef.current) {
      hasSeenNewlyAddedTagRef.current = true;
    }
  }, [hasSeenAccommodationsTour]);

  return (
    <>
      {isNewlyAdded && (
        <Box
          position="absolute"
          top={4}
          left={4}
          zIndex={1}
        >
          <Flex
            alignItems="center"
            gap={1}
            height={pxToRem(28)}
            paddingX={4}
            paddingY={0}
            borderRadius={pxToRem(16)}
            backgroundColor="newUIBrandPrimary"
            border="1px solid"
            borderColor="rgba(255, 255, 255, 0.5)"
            backdropFilter="blur(24px)"
            width="fit-content"
          >
            <Clock
              size={16}
              color="white"
            />
            <TextV2
              typographyVariant="label1"
              color="white"
              lineHeight={pxToRem(16)}
              fontSize={pxToRem(14)}
              fontWeight={500}
              whiteSpace="nowrap"
            >
              {tq`Newly Added`}
            </TextV2>
          </Flex>
        </Box>
      )}
      {!hideTravelTime && (
        <DistanceStatistics
          borderRadius={'full'}
          border="1px solid"
          borderColor="white"
          justifyContent="center"
          backgroundColor="mono2"
          backdropFilter={'blur(24px)'}
          position="absolute"
          top={12}
          left={isNewlyAdded ? undefined : 4}
          right={isNewlyAdded ? 4 : undefined}
          height={isNewlyAdded ? pxToRem(28) : undefined}
          fontSize={isNewlyAdded ? pxToRem(14) : undefined}
          paddingX={pxToRem(12)}
          paddingY={pxToRem(6)}
          zIndex={1}
          distances={distances}
          milesToVenue={milesToVenue}
        />
      )}
    </>
  );
};
